/**
 * Ecomm Display Name Service
 * Helper functions to get ecomm display names by service ID
 */

/**
 * Get ecomm display name by service ID
 * @param {Number|String} serviceId - Service ID
 * @param {Object} store - Vuex store instance
 * @returns {String|null} - Ecomm display name or service name or null if not found
 */
export function getEcommDisplayName(serviceId, store) {
    if (!serviceId || !store) return null;
    return store.getters['ecommDisplayNames/getEcommDisplayName'](serviceId);
}

/**
 * Get service name by ID
 * @param {Number|String} serviceId - Service ID
 * @param {Object} store - Vuex store instance
 * @returns {String|null} - Service name or null if not found
 */
export function getServiceName(serviceId, store) {
    if (!serviceId || !store) return null;
    return store.getters['ecommDisplayNames/getServiceName'](serviceId);
}

/**
 * Get full service object by ID
 * @param {Number|String} serviceId - Service ID
 * @param {Object} store - Vuex store instance
 * @returns {Object|null} - Service object or null if not found
 */
export function getServiceById(serviceId, store) {
    if (!serviceId || !store) return null;
    return store.getters['ecommDisplayNames/getServiceById'](serviceId);
}

/**
 * Get ecomm display name by service name (fallback method)
 * @param {String} serviceName - Service name
 * @param {Object} store - Vuex store instance
 * @returns {String|null} - Ecomm display name or service name or null if not found
 */
export function getEcommDisplayNameByName(serviceName, store) {
    if (!serviceName || !store) return null;
    return store.getters['ecommDisplayNames/getEcommDisplayNameByName'](serviceName);
}

/**
 * Vue Mixin for ecomm display names
 * Use this in components to access ecomm display name helpers
 */
export const ecommDisplayNameMixin = {
    methods: {
        /**
         * Get ecomm display name by service ID
         * @param {Number|String} serviceId - Service ID
         * @returns {String|null} - Ecomm display name or service name or null if not found
         */
        getEcommDisplayName(serviceId) {
            if (!serviceId || !this.$store) return null;
            const getter = this.$store.getters['ecommDisplayNames/getEcommDisplayName'];
            return getter ? getter(serviceId) : null;
        },
        /**
         * Get service name by ID
         * @param {Number|String} serviceId - Service ID
         * @returns {String|null} - Service name or null if not found
         */
        getServiceName(serviceId) {
            if (!serviceId || !this.$store) return null;
            const getter = this.$store.getters['ecommDisplayNames/getServiceName'];
            return getter ? getter(serviceId) : null;
        },
        /**
         * Get full service object by ID
         * @param {Number|String} serviceId - Service ID
         * @returns {Object|null} - Service object or null if not found
         */
        getServiceById(serviceId) {
            if (!serviceId || !this.$store) return null;
            const getter = this.$store.getters['ecommDisplayNames/getServiceById'];
            return getter ? getter(serviceId) : null;
        },
        /**
         * Get ecomm display name by service name (fallback method)
         * @param {String} serviceName - Service name
         * @returns {String|null} - Ecomm display name or service name or null if not found
         */
        getEcommDisplayNameByName(serviceName) {
            if (!serviceName || !this.$store) return null;
            const getter = this.$store.getters['ecommDisplayNames/getEcommDisplayNameByName'];
            return getter ? getter(serviceName) : null;
        },
        /**
         * Get ecomm display name for decoration service
         * Flow: decService.id (vendor service ID) -> find in VendorDecorationServices -> master_service_id -> ecomm_display
         */
        getDecorationServiceEcommDisplayName(decService) {
            if (!decService || !this.$store) return null;

            // Priority 1: Check ecomm_display directly on decService (if it's already enriched)
            if (decService.ecomm_display) {
                return decService.ecomm_display;
            }

            const decServiceId = decService.id ? (typeof decService.id === 'object' && decService.id.value ? decService.id.value : decService.id) : null;
            if (!decServiceId) return null;

            // Priority 2: Try to find vendor service from product data
            if (this.product && this.product.VendorDecorationServices && Array.isArray(this.product.VendorDecorationServices)) {
                const vendorService = this.product.VendorDecorationServices.find(
                    vs => {
                        const vsId = typeof vs.id === 'string' ? parseInt(vs.id) : vs.id;
                        const decId = typeof decServiceId === 'string' ? parseInt(decServiceId) : decServiceId;
                        return vsId === decId;
                    }
                );

                if (vendorService) {
                    // Try ecomm_display from vendor service
                    if (vendorService.ecomm_display) {
                        return vendorService.ecomm_display;
                    }
                    
                    // Priority 3: Try master_service_id lookup from store
                    if (vendorService.master_service_id) {
                        const masterServiceId = typeof vendorService.master_service_id === 'string' 
                            ? parseInt(vendorService.master_service_id) 
                            : vendorService.master_service_id;
                        const ecommDisplayName = this.getEcommDisplayName(masterServiceId);
                        if (ecommDisplayName) return ecommDisplayName;
                    }
                }
            }

            return null;
        },
        /**
         * Get ecomm display name for other service
         * Flow: otherService.id (vendor service ID) -> find in VendorOtherServices -> master_service_id -> ecomm_display
         */
        getOtherServiceEcommDisplayName(otherService) {
            if (!otherService || !this.$store) return null;

            // Priority 1: Check ecomm_display directly on otherService (if it's already from VendorOtherServices)
            if (otherService.ecomm_display) {
                return otherService.ecomm_display;
            }

            const otherServiceId = otherService.id ? (typeof otherService.id === 'object' && otherService.id.value ? otherService.id.value : otherService.id) : null;
            if (!otherServiceId) return null;

            // Priority 2: Try to find vendor service from product data
            if (this.product && this.product.VendorOtherServices && Array.isArray(this.product.VendorOtherServices)) {
                const vendorService = this.product.VendorOtherServices.find(
                    vs => {
                        const vsId = typeof vs.id === 'string' ? parseInt(vs.id) : vs.id;
                        const otherId = typeof otherServiceId === 'string' ? parseInt(otherServiceId) : otherServiceId;
                        return vsId === otherId;
                    }
                );

                if (vendorService) {
                    // Try ecomm_display from vendor service
                    if (vendorService.ecomm_display) {
                        return vendorService.ecomm_display;
                    }
                    
                    // Priority 3: Try master_service_id lookup from store
                    if (vendorService.master_service_id) {
                        const masterServiceId = typeof vendorService.master_service_id === 'string' 
                            ? parseInt(vendorService.master_service_id) 
                            : vendorService.master_service_id;
                        const ecommDisplayName = this.getEcommDisplayName(masterServiceId);
                        if (ecommDisplayName) return ecommDisplayName;
                    }
                }
            }

            return null;
        }
    }
};
